import { getFreshToken } from '../auth';
import { APP_CONFIG } from '../../config/app';
import { getActiveWorkspaceId, PERSONAL_WORKSPACE_ID } from './workspaceContext';

const PLET_API_URL = APP_CONFIG.apiUrl;

/**
 * Make an authenticated API request to Fokus backend
 *
 * options.workspaceId pins the request to the workspace that was active when
 * the operation was initiated (pass PERSONAL_WORKSPACE_ID or null for the
 * personal partition). Without it, the header is resolved from the live global
 * at fetch time — dangerous for queued/debounced writes, which could otherwise
 * land in a different workspace after a switch.
 */
export const pletApiRequest = async (endpoint, options = {}) => {
  const { workspaceId: pinnedWorkspaceId, signal, ...fetchOptions } = options;
  const token = await getFreshToken();
  const wsId =
    pinnedWorkspaceId !== undefined ? pinnedWorkspaceId : getActiveWorkspaceId();
  const headerWorkspaceId =
    wsId && wsId !== PERSONAL_WORKSPACE_ID ? wsId : null;
  const workspaceHeaders = headerWorkspaceId
    ? { 'X-Workspace-Id': headerWorkspaceId }
    : {};

  const timeoutSignal = !signal && typeof AbortSignal !== 'undefined' && AbortSignal.timeout
    ? AbortSignal.timeout(20000)
    : undefined;

  try {
    const response = await fetch(`${PLET_API_URL}${endpoint}`, {
      ...fetchOptions,
      signal: signal || timeoutSignal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...workspaceHeaders,
        ...fetchOptions.headers,
      },
    });

    // Never call response.json() blind: gateway errors (502/504, Lambda
    // timeouts) and dropped connections can return an empty or non-JSON
    // body, and the raw "Unexpected end of JSON input" then surfaces in the
    // UI while hiding the real status code.
    const text = await response.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }

    if (!response.ok) {
      const detail = [data?.error, data?.message].filter(Boolean).join(': ');
      const err = new Error(
        detail || `API request failed (HTTP ${response.status})`
      );
      err.status = response.status;
      err.code = data?.code || null;
      err.seats = data?.seats;
      throw err;
    }
    if (data === null) {
      throw new Error(
        `Server returned an invalid response (HTTP ${response.status}). Please try again.`
      );
    }

    return data;
  } catch (error) {
    if (
      (error.name === 'TypeError' && String(error.message || '').includes('fetch')) ||
      error.name === 'AbortError' ||
      error.name === 'TimeoutError'
    ) {
      console.warn('⚠️ Backend not available - API URL:', PLET_API_URL);
      throw new Error('Backend not available. Please check your connection.');
    }
    throw error;
  }
};
